import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { SelectOption } from 'Types/Option'
import { Search } from 'Assets/icons/Search'
import { InputWithIcon, CheckBox } from '@clickcease/cc-client-components-lib'
import { MultiDomainsTable, MultiDomainsTableContainer } from '../NewMultipleDomains.styles'
import { Products } from 'Types/Product'
import { SORT_COLUMNS } from '../helpers'
import { DomainsTableBody } from './DomainsTableBody'
import useAddDomainStore from '../store/addDomainsStore'

interface ManageAddedDomainsProps {
  domains: SelectOption[]
}

export const ManageAddedDomains = ({ domains }: ManageAddedDomainsProps) => {
  const { t } = useTranslation('domainManagement')
  const {
    setSelectedDomains,
    productsLeft,
    allowedProducts,
    allDomainsInTable,
    setAllDomainsInTable,
  } = useAddDomainStore()
  const containerRef = useRef<HTMLDivElement>(null)
  const [searchText, setSearchText] = useState<string>('')
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const [isDomainAdded, setIsDomainAdded] = useState<boolean>(false)

  const { botZapping: bmAllowed, paidMarketing: pmAllowed } = allowedProducts

  const addAvailableProducts = (address: string, pmLeft: boolean, bmLeft: boolean) => ({
    address,
    paidMarketing: pmLeft,
    botZapping: bmLeft,
    selected: pmLeft || bmLeft,
  })

  //  Add properties to new domains
  useEffect(
    () => {
      let pmLeft = pmAllowed ? productsLeft.paidMarketing : 0
      let bmLeft = bmAllowed ? productsLeft.botZapping : 0

      setAllDomainsInTable(
        domains.map((domain) => {
          const index = allDomainsInTable.findIndex(
            (prevDomain) => prevDomain.address === domain.value
          )
          if (index !== -1) return allDomainsInTable[index]
          setIsDomainAdded(true)
          const newDomain = addAvailableProducts(domain.value, !!pmLeft, !!bmLeft)
          if (pmLeft) pmLeft -= 1
          if (bmLeft) bmLeft -= 1
          return newDomain
        })
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [domains]
  )

  useEffect(() => {
    const selectedDomains = allDomainsInTable.filter(
      ({ selected, botZapping, paidMarketing }) => selected && (botZapping || paidMarketing)
    )
    if (allDomainsInTable.length && allDomainsInTable.length === selectedDomains.length)
      setSelectAll(true)

    // Scroll to the bottom of the container if a new domain is added
    if (isDomainAdded && containerRef.current) {
      setIsDomainAdded(false)
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }

    setSelectedDomains(selectedDomains)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDomainsInTable])

  const handleSetProducts =
    (address: string) => (products: { paidMarketing?: boolean; botZapping?: boolean }) => {
      const { paidMarketing: pmLeft, botZapping: bmLeft } = productsLeft

      const index = allDomainsInTable.findIndex((domain) => domain.address === address)
      const item = addAvailableProducts(
        address,
        Boolean(pmLeft && products.paidMarketing),
        Boolean(bmLeft && products.botZapping)
      )
      const domainsToSet =
        index === -1
          ? [...allDomainsInTable, item]
          : [...allDomainsInTable.slice(0, index), item, ...allDomainsInTable.slice(index + 1)]

      setAllDomainsInTable(domainsToSet)
    }

  const toggleSelectAll = () => {
    let pmLeft = productsLeft.paidMarketing
    let bmLeft = productsLeft.botZapping

    const updatePM = () => !!(--pmLeft >= 0)
    const updateBM = () => !!(--bmLeft >= 0)

    const newDomains = allDomainsInTable.map((domain) =>
      addAvailableProducts(domain.address, updatePM(), updateBM())
    )

    setAllDomainsInTable(newDomains)
    setSelectAll(!selectAll && newDomains.every((domain) => domain.selected))
  }

  // filter out products that not allowed
  const blockedDomains = Object.keys(allowedProducts).filter(
    (product) => !allowedProducts[product as Products]
  ) as Products[]

  return (
    <>
      <InputWithIcon
        css={{ width: 220, marginBottom: '$7' }}
        value={searchText}
        placeholder={t('search')}
        onChange={(e) => setSearchText(e.target.value)}
        iconRight={<Search width={12} height={12} />}
      />
      <MultiDomainsTableContainer ref={containerRef}>
        <MultiDomainsTable
          columnsNames={[
            <CheckBox isChecked={selectAll} onChange={toggleSelectAll} key="checkbox" />,
            t(SORT_COLUMNS.DOMAINS, { count: allDomainsInTable.length }),
            t(SORT_COLUMNS.PRODUCTS),
          ]}
          body={
            <DomainsTableBody
              blockedDomains={blockedDomains}
              searchText={searchText}
              handleSetProducts={handleSetProducts}
              setSelectAll={setSelectAll}
            />
          }
        />
      </MultiDomainsTableContainer>
    </>
  )
}
